import type { GemInclusionParams, GroupGemInclusionState } from "../../../shared/api-contract";

/**
 * Clarity — five grades of inner flaw for Basic's Diamond card, applied with
 * the viewer's webgi inclusion model (`setGroupGemInclusions`, contract v62).
 *
 * The maps are iJewel's own inclusion textures, mirrored into the VIEWER at
 * /materials/ijewel/tex/ (their CDN sends no CORS header, so the viewer serves
 * them from its own origin). The URLs must be absolute: the viewer loads them
 * from its document, not ours.
 *
 * iJewel ships no inclusion preset for a colourless diamond — every inclusion
 * `.dmat` it has is an emerald, ruby, sapphire or tourmaline
 * (webgl-jewelry-viewer/public/materials/ijewel/files/*-incl-*.dmat). The
 * grades are built from THOSE files' maps, tilings and strengths, each
 * source named below. Their maps grade naturally: `inclusions_normal_1` is a
 * few pinpoints, `_2` a handful of feathers, `_15` a dense web of scratches;
 * `inclusions_grunge_5` barely darkens, `_5b` clouds, `_3` is the darkest.
 *
 * One deliberate departure, measured on a clear round diamond
 * (OF/parity/studio-redesign/render-finetune-thumbs.mjs): the coloured-stone
 * files carry a crown haze (`surfaceRoughness` 0.08–0.21 plus roughness
 * maps). On a colourless stone that haze turns the whole stone milky — their
 * VVS-level set rendered as the CLOUDIEST of the five — so it is off for VVS
 * and VS and cut to 0.04 for SI, where a visible cloud is the point.
 */

export type ClarityId = "flawless" | "vvs" | "vs" | "si" | "included";

/** The viewer's origin — the same value `app/ViewerHost.tsx` points the iframe at. */
export const CLARITY_VIEWER_ORIGIN =
  (import.meta.env?.VITE_VIEWER_ORIGIN as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5174";

const tex = (file: string) => `${CLARITY_VIEWER_ORIGIN}/materials/ijewel/tex/${file}`;

/** Every inclusion map slot cleared and the path switched off: the stock
 *  ray-trace, bit-identical to a stone that never had inclusions. */
export const FLAWLESS_PAYLOAD: GemInclusionParams = {
  enabled: false,
  mapUrl: "",
  inclusionMapUrl: "",
  inclusionNormalMapUrl: "",
  inclusionRoughnessMapUrl: "",
  normalMapUrl: "",
  roughnessMapUrl: "",
  colorMapUrl: "",
};

/** A full webgi-layer state: every map slot named, so a grade never inherits a
 *  map from whatever the stone wore before (the setter is a patch). */
function grade(p: Required<Pick<GemInclusionParams,
  "inclusionMapUrl" | "inclusionNormalMapUrl" | "inclusionRoughnessMapUrl" | "normalMapUrl" | "roughnessMapUrl" |
  "inclusionScale" | "incNormalScale" | "surfaceScale" | "surfaceBumpScale" | "surfaceRoughness" | "blurRadius" |
  "variableBlur" | "variableParam1" | "variableParam2">>): GemInclusionParams {
  return { enabled: true, mapUrl: "", colorMapUrl: "", clampHDR: false, ...p };
}

export interface ClarityGrade {
  id: ClarityId;
  label: string;
  /** Plain words for the tooltip. */
  hint: string;
  params: GemInclusionParams;
}

export const CLARITY_GRADES: ClarityGrade[] = [
  { id: "flawless", label: "Flawless", hint: "No inner flaws", params: FLAWLESS_PAYLOAD },
  {
    // gem-sapphire-yellow-incl-03: no inclusion colour map, the pinpoint
    // normal map at its 0.185, its tiling 0.747. Haze off.
    id: "vvs", label: "VVS", hint: "Very, very slightly included: a few pinpoints",
    params: grade({
      inclusionMapUrl: "",
      inclusionNormalMapUrl: tex("inclusions_normal_1_01bc31fd63.jpg"),
      inclusionRoughnessMapUrl: "",
      normalMapUrl: "",
      roughnessMapUrl: "",
      inclusionScale: 0.747, incNormalScale: 0.185, surfaceScale: 1, surfaceBumpScale: 0.01,
      surfaceRoughness: 0, blurRadius: 0.1, variableBlur: 0, variableParam1: 1, variableParam2: 1,
    }),
  },
  {
    // gem-ruby-incl-07's inclusion map (grunge_5, the faintest) with the
    // feather normal map of 1-gem-emerald-incl-01 (normal_2) at 0.3. Haze off.
    id: "vs", label: "VS", hint: "Very slightly included: small feathers",
    params: grade({
      inclusionMapUrl: tex("inclusions_grunge_5_6382461295.jpg"),
      inclusionNormalMapUrl: tex("inclusions_normal_2_f2525e0d8f.jpg"),
      inclusionRoughnessMapUrl: "",
      normalMapUrl: "",
      roughnessMapUrl: "",
      inclusionScale: 1, incNormalScale: 0.3, surfaceScale: 1, surfaceBumpScale: 0.01,
      surfaceRoughness: 0, blurRadius: 0.1, variableBlur: 0, variableParam1: 1, variableParam2: 1,
    }),
  },
  {
    // 1-gem-ruby-incl-01: the cloud map (grunge_5b) for colour AND interior
    // roughness, the scratch web (normal_15) at 0.184, tiling 0.746, blur
    // 0.262. Crown haze 0.168 → 0.04 and its surface maps dropped.
    id: "si", label: "SI", hint: "Slightly included: a cloud you can see",
    params: grade({
      inclusionMapUrl: tex("inclusions_grunge_5b_ca84ee3cbb.jpg"),
      inclusionNormalMapUrl: tex("inclusions_normal_15_56771e082d.jpg"),
      inclusionRoughnessMapUrl: tex("inclusions_grunge_5b_ca84ee3cbb.jpg"),
      normalMapUrl: "",
      roughnessMapUrl: "",
      inclusionScale: 0.746, incNormalScale: 0.184, surfaceScale: 1, surfaceBumpScale: 0.01,
      surfaceRoughness: 0.04, blurRadius: 0.262, variableBlur: 0, variableParam1: 1, variableParam2: 1,
    }),
  },
  {
    // The darkest grunge in the library (grunge_3, the ruby files' roughness
    // map) as the inclusion map, with 1-gem-emerald-incl-01's large tiling
    // (0.4) and strongest normal strength (0.465) on the scratch web.
    id: "included", label: "Included", hint: "Included: dark flaws plain to see",
    params: grade({
      inclusionMapUrl: tex("inclusions_grunge_3_d96d9c2b03.jpg"),
      inclusionNormalMapUrl: tex("inclusions_normal_15_56771e082d.jpg"),
      inclusionRoughnessMapUrl: "",
      normalMapUrl: "",
      roughnessMapUrl: "",
      inclusionScale: 0.4, incNormalScale: 0.465, surfaceScale: 1, surfaceBumpScale: 0.01,
      surfaceRoughness: 0, blurRadius: 0.1, variableBlur: 0, variableParam1: 1, variableParam2: 1,
    }),
  },
];

/** Advanced's gem-inclusion row, for "See it". */
export const CLARITY_ROW = "materials.diamondRayTracing.inclusions";

const near = (a: number | undefined, b: number | undefined) => a !== undefined && b !== undefined && Math.abs(a - b) < 1e-6;

/** Which grade a stone's inclusion state is, or null for inclusions no grade describes. */
export function clarityOfState(st: GroupGemInclusionState | GemInclusionParams | null): ClarityId | null {
  if (!st || !st.enabled) return "flawless";
  const hit = CLARITY_GRADES.find(
    (g) =>
      g.id !== "flawless" &&
      (g.params.inclusionMapUrl ?? "") === (st.inclusionMapUrl ?? "") &&
      (g.params.inclusionNormalMapUrl ?? "") === (st.inclusionNormalMapUrl ?? "") &&
      near(g.params.incNormalScale, st.incNormalScale) &&
      near(g.params.inclusionScale, st.inclusionScale) &&
      !st.mapUrl,
  );
  return hit?.id ?? null;
}
